import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AdminTicketCategory } from '@central/contracts';
import { Button } from '@/components/Button';
import {
  alterarCategoria,
  apagarCategoria,
  chavesDeAdministracao,
  criarCategoria,
  listarCategorias,
  reordenarCategorias,
} from '@/features/admin/admin.api';
import {
  AcoesDaLinha,
  BotoesDeOrdem,
  Cabecalho,
  Campo,
  CascaDeCatalogo,
  Celula,
  Chave,
  chaveSugerida,
  CLASSE_DE_CAMPO,
  ConfirmacaoDeCatalogo,
  ContagemDeUso,
  EtiquetaDeAtividade,
  Linha,
  LinhaDeCabecalho,
  useEscritaDeCatalogo,
  type Intencao,
} from '@/features/admin/componentes';
import { Modal } from '@/features/tickets/Modal';

/**
 * Categorias de chamado — o primeiro dos dois níveis de classificação.
 *
 * A coluna "subcategorias" não é decoração: é o que explica por que o botão de
 * remover está desabilitado numa categoria que nenhum chamado usa. Sem ela, o
 * 409 do servidor chegaria como surpresa.
 */
export function TicketCategoriesPage() {
  const consulta = useQuery({
    queryKey: chavesDeAdministracao.categorias,
    queryFn: listarCategorias,
  });
  const categorias = consulta.data?.items ?? [];

  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<AdminTicketCategory | null>(null);
  const [confirmando, setConfirmando] = useState<{
    registro: AdminTicketCategory;
    intencao: Intencao;
  } | null>(null);

  const ordenacao = useEscritaDeCatalogo(reordenarCategorias);
  const atividade = useEscritaDeCatalogo(
    ({ id, active, version }: { id: string; active: boolean; version: number }) =>
      alterarCategoria(id, { active, version }),
    () => setConfirmando(null),
  );
  const remocao = useEscritaDeCatalogo(
    (id: string) => apagarCategoria(id),
    () => setConfirmando(null),
  );

  const ocupado =
    ordenacao.mutacao.isPending || atividade.mutacao.isPending || remocao.mutacao.isPending;

  function mover(indice: number, direcao: -1 | 1) {
    const ids = categorias.map((c) => c.id);
    const destino = indice + direcao;
    [ids[indice], ids[destino]] = [ids[destino] as string, ids[indice] as string];
    ordenacao.mutacao.mutate(ids);
  }

  return (
    <>
      <CascaDeCatalogo
        titulo="Categorias"
        resumo="O primeiro dos dois níveis de classificação. A ordem daqui é a ordem do seletor na abertura de chamado."
        rotuloDoBotao="Nova categoria"
        aoCriar={() => setCriando(true)}
        consulta={consulta}
        vazio="Nenhuma categoria cadastrada. A abertura de chamado pela tela exige uma."
      >
        <LinhaDeCabecalho>
          <Cabecalho className="w-24">Ordem</Cabecalho>
          <Cabecalho>Categoria</Cabecalho>
          <Cabecalho>Chave</Cabecalho>
          <Cabecalho>Subcategorias</Cabecalho>
          <Cabecalho>Em uso</Cabecalho>
          <Cabecalho>Estado</Cabecalho>
          <Cabecalho className="text-right">Ações</Cabecalho>
        </LinhaDeCabecalho>

        <tbody>
          {categorias.map((categoria, indice) => (
            <Linha key={categoria.id} inativo={!categoria.active}>
              <Celula>
                <BotoesDeOrdem
                  primeira={indice === 0}
                  ultima={indice === categorias.length - 1}
                  ocupado={ocupado}
                  aoSubir={() => mover(indice, -1)}
                  aoDescer={() => mover(indice, 1)}
                />
              </Celula>

              <Celula>
                <span className="flex items-center gap-2 font-medium">
                  {categoria.color ? (
                    <span
                      aria-hidden
                      className="h-2.5 w-2.5 flex-none rounded-full ring-1 ring-inset ring-border"
                      style={{ backgroundColor: categoria.color }}
                    />
                  ) : null}
                  {categoria.name}
                </span>
                {categoria.description ? (
                  <span className="mt-0.5 block max-w-md truncate text-xs text-muted-foreground">
                    {categoria.description}
                  </span>
                ) : null}
              </Celula>

              <Celula>
                <Chave valor={categoria.key} />
              </Celula>

              <Celula className="text-xs tabular-nums">
                {categoria.usage.children === 0 ? (
                  <span className="text-muted-foreground">nenhuma</span>
                ) : (
                  categoria.usage.children
                )}
              </Celula>

              <Celula>
                <ContagemDeUso uso={categoria.usage} />
              </Celula>

              <Celula>
                <EtiquetaDeAtividade ativo={categoria.active} />
              </Celula>

              <Celula>
                <AcoesDaLinha
                  registro={categoria}
                  entidade="categoria"
                  ocupado={ocupado}
                  aoEditar={() => setEditando(categoria)}
                  aoAlternarAtividade={() => {
                    if (!categoria.active) {
                      atividade.mutacao.mutate({
                        id: categoria.id,
                        active: true,
                        version: categoria.version,
                      });
                      return;
                    }
                    setConfirmando({ registro: categoria, intencao: 'desativar' });
                  }}
                  aoRemover={() => setConfirmando({ registro: categoria, intencao: 'remover' })}
                />
              </Celula>
            </Linha>
          ))}
        </tbody>
      </CascaDeCatalogo>

      {ordenacao.erro ? (
        <p role="alert" className="text-sm text-destructive">
          {ordenacao.erro}
        </p>
      ) : null}

      <DialogoDeCategoria
        aberto={criando || editando !== null}
        categoria={editando}
        aoFechar={() => {
          setCriando(false);
          setEditando(null);
        }}
      />

      <ConfirmacaoDeCatalogo
        aberto={confirmando !== null}
        intencao={confirmando?.intencao ?? 'desativar'}
        entidade="categoria"
        registro={confirmando?.registro ?? null}
        erro={atividade.erro ?? remocao.erro}
        ocupado={ocupado}
        aoFechar={() => setConfirmando(null)}
        aoConfirmar={() => {
          if (!confirmando) return;
          if (confirmando.intencao === 'remover') {
            remocao.mutacao.mutate(confirmando.registro.id);
            return;
          }
          atividade.mutacao.mutate({
            id: confirmando.registro.id,
            active: false,
            version: confirmando.registro.version,
          });
        }}
      />
    </>
  );
}

function DialogoDeCategoria({
  aberto,
  categoria,
  aoFechar,
}: {
  aberto: boolean;
  categoria: AdminTicketCategory | null;
  aoFechar: () => void;
}) {
  const editando = categoria !== null;

  const [nome, setNome] = useState('');
  const [chave, setChave] = useState('');
  const [chaveTocada, setChaveTocada] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [semeado, setSemeado] = useState<string | null>(null);

  const identidade = categoria?.id ?? (aberto ? 'nova' : null);
  if (aberto && semeado !== identidade) {
    setSemeado(identidade);
    setNome(categoria?.name ?? '');
    setChave(categoria?.key ?? '');
    setChaveTocada(categoria !== null);
    setDescricao(categoria?.description ?? '');
  }
  if (!aberto && semeado !== null) setSemeado(null);

  const escrita = useEscritaDeCatalogo(async () => {
    if (categoria) {
      return alterarCategoria(categoria.id, {
        name: nome.trim(),
        description: descricao.trim(),
        version: categoria.version,
      });
    }
    return criarCategoria({
      key: chave.trim(),
      name: nome.trim(),
      ...(descricao.trim() ? { description: descricao.trim() } : {}),
    });
  }, aoFechar);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={editando ? `Editar “${categoria.name}”` : 'Nova categoria'}
      descricao={
        editando
          ? 'A chave não muda: integração, URL e automação a usam para achar este registro.'
          : 'A chave é definitiva. O nome pode ser corrigido depois.'
      }
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <Campo id="categoria-nome" rotulo="Nome">
          <input
            id="categoria-nome"
            value={nome}
            onChange={(evento) => {
              setNome(evento.target.value);
              if (!chaveTocada) setChave(chaveSugerida(evento.target.value));
            }}
            required
            minLength={2}
            maxLength={80}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo
          id="categoria-chave"
          rotulo="Chave"
          dica={editando ? 'Imutável depois de criada.' : 'Minúsculas, dígitos, ponto e hífen.'}
        >
          <input
            id="categoria-chave"
            value={chave}
            onChange={(evento) => {
              setChaveTocada(true);
              setChave(evento.target.value);
            }}
            disabled={editando}
            required
            pattern="[a-z0-9][a-z0-9.\-]*[a-z0-9]"
            minLength={2}
            maxLength={60}
            className={`${CLASSE_DE_CAMPO} font-mono text-xs`}
          />
        </Campo>

        <Campo id="categoria-descricao" rotulo="Descrição" opcional>
          <textarea
            id="categoria-descricao"
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
            rows={2}
            maxLength={500}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            {editando ? 'Salvar' : 'Criar categoria'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
